/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import {useEffect, useState} from "react";

// material
import {Page, Content, InContentMessage, history, InlineMessage} from "@insticator/insticator-ui";
import {ListTable} from "@insticator/insticator-ui-classic"
import DisabledGlobalFilter from "../monetizationform/filter/disabledglobalfilter/disabledglobalfilter";
import Header from "../../../../material/structure/header/header";
import PageHeader from "../../../../material/structure/pageheader/pageheader";
import ExpandableSection from "../../../../material/expandablesection/expandablesection";
import LoadingState from "../../../../material/states/loading/loading";
import SectionCard from "../../../../material/expandablesection/sectioncard/sectioncard";
import ConfigController from "../../../../material/expandablesection/controllers/configcontroller/configcontroller";

// redux
import {useDispatch, useSelector} from "react-redux";

// thunks
import {resetPreviewDownloadStatus, setIsPreviewing,setPreviewVisited} from "../../../../state/slices/report";
import {createReport, getPreviewReport, updateReport} from "../../../../state/thunks/report";
import account, {toggleIsOpened} from "../../../../state/slices/account";

// styles
import injectSheet from 'react-jss';
import styles from './reportpreview_styles';

// utilities
import sortFunction from './previewsortfunction';
import {formDiff,validate} from "../monetizationform/utils/validateform";
import {getPreviewPayload, getSavePayload} from "./utils/reportpayload";

// static
import {
  CANCEL_TITLE, NEW_REPORT_UUID, REPORT_NAME, REPORT_UUID, SAVE_TITLE
} from "../monetizationform/consts";
import {columnHeadsNames} from "./consts";
const ERROR_MSG = 'Please request a preview again. If this error persists, contact your account manager.'
const NO_DATA_MSG = 'No data available for this date range.'
const IDLE_MSG = 'Please request the preview again'


/* ========== ~~~~~~~~~~ REPORT BUILDER / REPORT PREVIEW ~~~~~~~~~~ ========== */
export default injectSheet(styles)(({classes}) => {

  const previewData = useSelector((state) => state.report.previewData)
  const query = useSelector((state) => state.router.location.query);
  const reportUUID = query['reportUUID'];
  const accountUUID = useSelector((state) => state.session.accountUUID);
  const formValues = useSelector((state) => state.report[reportUUID]);
  const allReports = useSelector((state) => state.report.allReports);
  const formValuesFromDB = useSelector((state) => state.report.allReports.find(r => r.reportUUID === reportUUID));
  const reportNames = allReports.filter(r => r.reportUUID !== reportUUID).map(r => r[REPORT_NAME] && r[REPORT_NAME].toLowerCase());
  const [currentSortedColumn, setCurrentSortedColumn] = useState('')
  const [rowData, setRowData] = useState([])
  const dispatch = useDispatch();
  const previewRequestStatus = useSelector((state) => state.report.previewRequestStatus)
  const downloadStatus = useSelector((state) => state.report.previewDownloadStatus);
  const reportPreviewVisited = useSelector((state) => state.report.reportPreviewVisited);
  const sitesAndAppsUUIDs = useSelector((state) => state.account.sitesAndAppsUUIDs);
  const [canSave, setCanSave] = useState(false);

  useEffect(() => {
    if (!formValues || !accountUUID) return
    dispatch(getPreviewReport(getPreviewPayload(formValues, accountUUID, 'JSON')))
  }, [])

  useEffect(() => {
    dispatch(setPreviewVisited(true));
  },[])

  useEffect(() => {
    if (downloadStatus === 'success' || downloadStatus === 'error') setTimeout(() => {
      dispatch(resetPreviewDownloadStatus())
    }, 2000);
  }, [downloadStatus])

  /********** Format table data **********/
  const isNewForm = reportUUID === NEW_REPORT_UUID;
  // Remove extra fields generated in RMS by default
  const totals = previewData.total
  const dimensions = totals?.dimensions ? totals.dimensions : [];
  const metrics = totals?.metricTotals ? Object.keys(totals.metricTotals) : [];
  const metricTotals = totals?.metricTotals;
  useEffect(() => {
    if (!previewData.data) return
    let dataCopy = JSON.parse(JSON.stringify(previewData.data))
    dataCopy.forEach(row => {
      Object.keys(row).forEach((key) => {
        dimensions.includes(key) || metrics.includes(key) || delete row[key]
      })
    })
    setRowData(dataCopy)
  }, [previewData])

  useEffect(() => {
    if (validate(formValues, formValuesFromDB, reportNames)) setCanSave(true)
    else setCanSave(false)
  }, [formValues, reportNames])

  const isDownloading = downloadStatus && downloadStatus !== 'idle';
  const handleDownload = () => {
    if (isDownloading) return
    const format = formValues.schedule.format || 'CSV';
    const payload  = getPreviewPayload(formValues, accountUUID, format);
    dispatch(getPreviewReport(payload));
  }

  const handleCancel = () => {
    dispatch(setIsPreviewing(false))
    history.goBack()
  }

  const handleSave = () => {
    const payload = getSavePayload(formValues, accountUUID, sitesAndAppsUUIDs)
    if (canSave) {
      if (isNewForm) {
        delete payload[REPORT_UUID]
        dispatch(createReport(payload))
      } else {
        dispatch(toggleIsOpened(payload[REPORT_UUID]))
        dispatch(updateReport(payload))
      }
    }
    dispatch(setIsPreviewing(false))
    history.push('/dashboard/reportbuilder/allreports');
  }

  const handleColumnSort = (func, columnHead) => {
    const sorted = currentSortedColumn === columnHead ? '' : columnHead;
    let dataCopy = JSON.parse(JSON.stringify(previewData.data))
    setRowData(func(dataCopy, !!sorted))
    setCurrentSortedColumn(sorted)
  };

  /********** Error Page **********/
  if (previewRequestStatus === 'pending') return <LoadingState />
  if (previewRequestStatus === 'error' || !previewData || Object.keys(previewData).length === 0) {
    const message = (() => {
      switch(previewRequestStatus) {
        case 'error': return ERROR_MSG
        case 'success': return NO_DATA_MSG
        default: return IDLE_MSG
      }
    })();

    return (
      <Page name={'Report Builder'}>
        <Header>
          <DisabledGlobalFilter/>
        </Header>
        <Content>
          <PageHeader title={'Report Builder'}/>
          <ExpandableSection
              isOpened={true}
              handleCancel={handleCancel}
              cancelTitle={CANCEL_TITLE}
              handleSave={canSave && handleSave}
              saveTitle={SAVE_TITLE}
              isChanged={canSave}
          >
          </ExpandableSection>
          <InlineMessage
            icon={'exclamation-triangle'}
            variant={'system'}
            message={message}
            handleClose={() => null}
            />
        </Content>
      </Page>
    )
  }

  /********** Continue formatting table data (preview data now verified non-empty) **********/
  // populate column heads
  let columnHeads = {};

  // Set column heads
  if(previewData.data?.length) Object.keys(previewData.data[0]).forEach(key => {
    columnHeads[key] = !!columnHeadsNames[key] ? columnHeadsNames[key] : key;
  });

  const specialHeads = [
    { colspan: dimensions.length, colcontent: `Dimensions` },
    { colspan: metrics.length, colcontent: `Metrics` }
  ];
  const specialRow = [
    { colspan: dimensions.length, colcontent: `Total` },
    { colspan: null, colcontent: metricTotals }
  ];

  /********** Display Page **********/
  const controller = (
    <ConfigController
      isOpened={true}
      handleCancel={handleCancel}
      title='Report Preview'
      cancelTitle={CANCEL_TITLE}
      isChanged={canSave}
      handleSave={handleSave}
      saveTitle={SAVE_TITLE}
    />
  )

  return (
    <Page name={'Report Builder'}>
      <Header>
        <DisabledGlobalFilter/>
      </Header>
      <Content>
        <InContentMessage />
        <PageHeader title={'Report Builder'}/>
          <ExpandableSection
            isOpened={true}
            controller={controller}
            handleCancel={handleCancel}
            cancelTitle={CANCEL_TITLE}
            handleDownload={handleDownload}
            isDownloading={isDownloading}
            handleSave={handleSave}
            saveTitle={SAVE_TITLE}
            isChanged={canSave}
          >
            <SectionCard>
              <div className={classes.preview}>
                <InlineMessage
                  icon={'exclamation-triangle'}
                  variant={'system'}
                  message={`Previewing only the first ${rowData?.length} rows. Download the report for full access.`}
                  handleClose={() => null}
                />
                <ListTable
                  name={`reportpreview`}
                  variant={`slim`}
                  type={'basic'}
                  size={`full`}
                  specialHeads={specialHeads}
                  columnHeads={columnHeads}
                  sortableHeads={Object.keys(columnHeadsNames)}
                  onSort={e => handleColumnSort(sortFunction(e), e)}
                  specialRow={specialRow}
                  specialRowPosition={`bottom`}
                  data={rowData}
                  showTitle={false}
                  exportData={false}
                  moreDetails={false}
                />
                <p className="rowCount">{rowData?.length} of {totals?.rows} rows displayed</p>
              </div>
            </SectionCard>
          </ExpandableSection>
      </Content>
    </Page>
  )
});